import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Mono } from '../../../../../../generic/ui/Mono';
import { Tag } from '../../../../../../generic/ui/Tag';
import InvoiceIcon from '../../../../../../generic/icons/IconLib/InvoiceIcon';
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import FillGauge from '../../Components/FillGauge';

export const CUSTOMER_ID_COLOR = '#8CC53D';

interface Props {
    likutInfo: any;
    isAddIdToDesc: boolean;
    persents: number;
    progressColor: string;
    existWeigthQty: number | null;
    existArizotQty: number | null;
    onOpenDetails: () => void;
}

const LikutSectionHeader: React.FC<Props> = ({
    likutInfo,
    isAddIdToDesc,
    persents,
    progressColor,
    existWeigthQty,
    existArizotQty,
    onOpenDetails,
}) => {
    const [t] = useTranslation(['likut']);
    return (
        <HeaderCard
            type='button'
            onClick={onOpenDetails}
            aria-label={t('likut_modal_order_details_title')}
            title={t('likut_modal_order_details_title')}
        >
            <InfoColumn>
                <InfoLine>
                    <InfoIconSlot>
                        <InvoiceIcon color='currentColor' size={16} />
                    </InfoIconSlot>
                    <PrimaryValue>{likutInfo?.azmanaNum}</PrimaryValue>
                </InfoLine>
                <InfoLine>
                    {/* <InfoIconSlot>
                        <UserIcon color='currentColor' size={16} />
                    </InfoIconSlot> */}
                    {isAddIdToDesc && likutInfo?.clientId != null && (
                        <Tag variant='tinted' color={CUSTOMER_ID_COLOR}>
                            {likutInfo.clientId}
                        </Tag>
                    )}
                    <ClientName>{likutInfo?.clientName?.trim()}</ClientName>
                </InfoLine>
            </InfoColumn>
            <MetricsColumn>
                <MetricLine>
                    <InfoIconSlot>
                        <ScaleIcon color='currentColor' size={14} />
                    </InfoIconSlot>
                    <MetricValue>{existWeigthQty?.toFixed(2)}</MetricValue>
                    <UnitLabel>ק"ג</UnitLabel>
                </MetricLine>
                <MetricLine>
                    <InfoIconSlot>
                        <PackageBoxIcon color='currentColor' size={14} />
                    </InfoIconSlot>
                    <MetricValue>{existArizotQty}</MetricValue>
                    {/* <UnitLabel>יח</UnitLabel> */}
                </MetricLine>
            </MetricsColumn>
            <GaugeSlot>
                <FillGauge percent={persents} color={progressColor || undefined} size={52} />
            </GaugeSlot>
        </HeaderCard>
    );
};

export default LikutSectionHeader;

const HeaderCard = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
    direction: rtl;
    padding: 0.75rem 1rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.lg};
    box-shadow: ${({ theme }) => theme.shadows.card};
    cursor: pointer;
    flex-shrink: 0;
    text-align: right;
    transition: border-color ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
    }

    &:active {
        transform: translateY(1px);
    }
`;

const GaugeSlot = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    margin-inline-start: auto;
`;

const InfoColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const MetricsColumn = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const InfoLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
`;

const MetricLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const InfoIconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const PrimaryValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const ClientName = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetricValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const UnitLabel = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
`;
